import { token } from '@atlas-frontend/design-tokens';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { Button, Descriptions, Divider, Flex } from 'antd';
import { DescriptionsItemType } from 'antd/es/descriptions';
import { useMemo } from 'react';
import styled from 'styled-components';
import { Main, Padding, SidePanel } from '../../common/components';
import {
  useSystemClassifierItem,
  useSystemClassifierItemById,
} from '../../configuration';
import { DataSourceFormDataType } from '../schema';
import translations from '../translations/DataSourceViewPanel.translations.json';

const { addTranslations, useTranslation } = i18nNsFactory('data-sources-table');
addTranslations(translations);

export interface DataSourceViewPanelProps {
  dataSource: DataSourceFormDataType;
  onClose: () => void;
}

export function DataSourceViewPanel({
  dataSource,
  onClose,
}: DataSourceViewPanelProps) {
  const { t } = useTranslation();
  const sourceTypeClassifier = useSystemClassifierItem(
    'DATA_SOURCE_TYPE',
    dataSource.sourceType ?? '',
  );

  const urlSource = dataSource.sourceType === 'URL' ? dataSource : null;
  const methodName = useSystemClassifierItemById(
    'QUERY_METHOD_NAME',
    urlSource?.methodNameId,
  );
  const dataItems = useMemo(() => {
    const isUrlPost = urlSource != null && methodName?.code === 'POST';

    const columns: DescriptionsItemType[] = [
      {
        key: 'dataLocation',
        label: t(`fields.dataLocation.${dataSource.sourceType}`),
        children: dataSource.dataLocation,
      },

      ...(urlSource
        ? [
            {
              key: 'methodNameId',
              label: t(`fields.methodNameId.label`),
              children: methodName?.name,
            },
          ]
        : []),

      ...(isUrlPost && urlSource
        ? [
            {
              key: 'contentType',
              label: t('fields.contentType.label'),
              children: urlSource.contentType,
            },
            {
              key: 'contentBody',
              label: t('fields.contentBody.label'),
              children: urlSource.contentBody,
            },
          ]
        : []),

      ...(dataSource.groupSource
        ? [
            {
              key: 'groupSource',
              label: t(`fields.groupSource.label`),
              children: t(`fields.groupSource.yes`),
            },
          ]
        : []),

      {
        key: 'connectionId',
        label: t('fields.connectionId.label'),
        children: dataSource.connectionId,
      },
    ];

    if (dataSource.sourceType === 'DATABASE') {
      if (dataSource.groupSource) {
        columns.push({
          key: 'dataObjectCodeColumnName',
          label: t('fields.dataObjectCodeColumnName.label'),
          children: dataSource.dataObjectCodeColumnName,
        });
      }
      columns.push(
        {
          key: 'valueDateColumnName',
          label: t('fields.valueDateColumnName.label'),
          children: dataSource.valueDateColumnName,
        },
        {
          key: 'subjectCodeColumnName',
          label: t('fields.subjectCodeColumnName.label'),
          children: dataSource.subjectCodeColumnName,
        },
        {
          key: 'code',
          label: t('fields.code.label'),
          children: dataSource.code,
        },
        {
          key: 'name',
          label: t('fields.name.label'),
          children: dataSource.name,
        },
      );
    }

    return columns;
  }, [dataSource, urlSource, methodName, t]);

  return (
    <SidePanel
      side="right"
      width={1786}
      minWidth={450}
      collapsedWidth={450}
      mode="layered"
    >
      <SidePanelWrapper>
        <ScrollContent>
          <Flex vertical gap="small">
            <h3>{t('dataSources.title')}</h3>
            <div>
              <Divider orientation="left">{t('section.source')}</Divider>
              <Descriptions
                column={1}
                items={[
                  {
                    key: 'sourceType',
                    label: t('fields.sourceType.label'),
                    children: sourceTypeClassifier?.name,
                  },
                ]}
              />
              <Divider orientation="left">{t('section.data')}</Divider>
              <Descriptions column={1} items={dataItems} layout="vertical" />
              <Divider />
              <Descriptions
                column={1}
                items={[
                  {
                    key: 'description',
                    label: t('fields.description.label'),
                    children: dataSource.description,
                  },
                ]}
                layout="vertical"
              />
            </div>
          </Flex>
        </ScrollContent>
        <Main.Footer>
          <FullWidthFlex justify="end" gap="small">
            <Button id="close-dataSource" onClick={onClose} type="primary">
              {t('button.close')}
            </Button>
          </FullWidthFlex>
        </Main.Footer>
      </SidePanelWrapper>
    </SidePanel>
  );
}

const FullWidthFlex = styled(Flex)`
  width: 100%;
`;

const SidePanelWrapper = styled.div`
  display: grid;
  grid-template-rows: auto 60px;
  height: 100%;
  width: 100%;
  overflow: hidden;
  border-left: 1px solid ${token.colorBorderSecondary};

  .ant-descriptions-middle .ant-descriptions-row > th {
    padding-bottom: 0;
  }
`;

const ScrollContent = styled(Padding)`
  overflow-y: auto;
`;
